// src/components/Bracket/ThreeGGBracketView.jsx
//
// Data-driven 3GG double-elimination view.
// - Splits games by bracketLevel (WINNERS / LOSERS / GRAND_FINALS)
// - One aligned header row per bracket (round labels across the top)
// - Empty slots show "Winner/Loser of Slot N", derived by reverse-looking-up
//   which game feeds each position via nextGameId + nextGamePosition
// - Cards match the reference spec: field/time inline on team rows (unscored),
//   score + blue winner highlight + green check (scored), slot id bottom-left
// - Connectors derived from nextGameId (works for any bracket size)

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import logo from "../../assets/images/usmlsa_new.png";
import "./ThreeGGBracketView.css";

export default function ThreeGGBracketView({
  games = [],
  isAdmin,
  onEnterScore,
  onEditGame,
  bracket,
  onPublish,
  onDelete,
  publishing,
}) {
  const [zoom, setZoom] = useState(0.8);

  const status = bracket?.bracketStatus;
  const isDraft = status === "DRAFT";
  const showSeeds = bracket?.showSeeds !== false; // default true unless explicitly false

  // ---- split by level -----------------------------------------------------
  const { winners, losers, grandFinals } = useMemo(() => {
    const wb = [], lb = [], gf = [];
    for (const g of games) {
      const level = (g.bracketLevel || "").toUpperCase();
      if (level === "LOSERS") lb.push(g);
      else if (level.startsWith("GRAND")) gf.push(g);
      else wb.push(g);
    }
    return { winners: wb, losers: lb, grandFinals: gf };
  }, [games]);

  const winnersPlusGF = useMemo(() => [...winners, ...grandFinals], [winners, grandFinals]);

  // ---- group into rounds --------------------------------------------------
  const toRounds = useCallback((list) => {
    const byRound = new Map();
    for (const g of list) {
      const r = g.roundNumber ?? 0;
      if (!byRound.has(r)) byRound.set(r, []);
      byRound.get(r).push(g);
    }
    for (const arr of byRound.values()) {
      arr.sort((a, b) => (a.gamePosition ?? a.slotNum ?? 0) - (b.gamePosition ?? b.slotNum ?? 0));
    }
    return [...byRound.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([round, gs]) => ({
        round,
        games: gs,
        // Use the template's round_description if present (e.g. "Winners R1"),
        // falling back to a generic label.
        label: gs[0]?.roundDescription || `Round ${round}`,
      }));
  }, []);

  const wbRounds = useMemo(() => toRounds(winnersPlusGF), [winnersPlusGF, toRounds]);
  const lbRounds = useMemo(() => toRounds(losers), [losers, toRounds]);

  // ---- connectors ---------------------------------------------------------
  const buildConnections = useCallback((list) => {
    const ids = new Set(list.map((g) => g.bracketGameId));
    const conns = [];
    for (const g of list) {
      if (g.nextGameId && ids.has(g.nextGameId)) conns.push({ from: g.bracketGameId, to: g.nextGameId });
    }
    return conns;
  }, []);

  const wbConnections = useMemo(() => buildConnections(winnersPlusGF), [winnersPlusGF, buildConnections]);
  const lbConnections = useMemo(() => buildConnections(losers), [losers, buildConnections]);

  return (
    <div className="gg3-container">
      {/* Header: title + actions + zoom */}
      <div className="gg3-header">
        <div className="gg3-header-left">
          <div className="gg3-title-row">
            <img
              src={logo}
              alt="USMLSA"
              className="gg3-logo"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <h1 className="gg3-title">{bracket?.bracketName || "Bracket"}</h1>
          </div>
          <div className="gg3-subtitle">
            {bracket?.tournamentName} — {bracket?.divisionName} · 3GG Double Elimination
          </div>
        </div>
        <div className="gg3-header-right">
          {isAdmin && (
            <div className="gg3-actions">
              {isDraft && onPublish && (
                <button className="gg3-btn gg3-btn-primary" onClick={onPublish} disabled={publishing}>
                  {publishing ? "Publishing…" : "↗ Publish Bracket"}
                </button>
              )}
              {onDelete && (
                <button className="gg3-btn gg3-btn-danger" onClick={onDelete}>🗑 Delete Bracket</button>
              )}
            </div>
          )}
          <div className="gg3-controls">
            <button onClick={() => setZoom((z) => Math.min(1.5, +(z + 0.1).toFixed(2)))}>+</button>
            <button onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))}>−</button>
            <span className="gg3-zoom">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom(0.8)}>Reset</button>
          </div>
        </div>
      </div>

      {isDraft && (
        <div className="gg3-draft-banner">
          <strong>⚠ Draft — not on the schedule yet</strong>
          <span>Only admins can see this. Review matchups and seeding, then publish when ready.</span>
        </div>
      )}

      <div className="gg3-section-title wb">
        Winners Bracket ({winners.length} game{winners.length !== 1 ? "s" : ""})
      </div>
      <BracketViewport
        rounds={wbRounds}
        connections={wbConnections}
        zoom={zoom}
        color="#0066cc"
        showHeaders={true}          /* headers only on winners, per request */
        showSeeds={showSeeds}
        isAdmin={isAdmin}
        onEnterScore={onEnterScore}
        onEditGame={onEditGame}
      />

      <div className="gg3-section-title lb">
        Losers Bracket ({losers.length} game{losers.length !== 1 ? "s" : ""})
      </div>
      <BracketViewport
        rounds={lbRounds}
        connections={lbConnections}
        zoom={zoom}
        color="#ff9900"
        showHeaders={false}
        showSeeds={showSeeds}
        isAdmin={isAdmin}
        onEnterScore={onEnterScore}
        onEditGame={onEditGame}
      />
    </div>
  );
}

// ===========================================================================
function BracketViewport({ rounds, connections, zoom, color, showHeaders, showSeeds, isAdmin, onEnterScore, onEditGame }) {
  const innerRef = useRef(null);
  const svgRef = useRef(null);

  useEffect(() => {
    const draw = () => drawConnectors(innerRef.current, svgRef.current, connections, zoom, color);
    const t1 = setTimeout(draw, 60);
    const t2 = setTimeout(draw, 260);
    const onResize = () => draw();
    window.addEventListener("resize", onResize);
    return () => { clearTimeout(t1); clearTimeout(t2); window.removeEventListener("resize", onResize); };
  }, [connections, zoom, color, rounds]);

  return (
    <div className="gg3-viewport">
      <div className="gg3-scaler" style={{ transform: `scale(${zoom})` }}>
        <div className="gg3-inner" ref={innerRef}>
          {/* Aligned header row (winners only) */}
          {showHeaders && (
            <div className="gg3-header-row">
              {rounds.map(({ round, label }) => (
                <div className="gg3-col-header" key={round}>{label}</div>
              ))}
            </div>
          )}

          <svg className="gg3-connectors" ref={svgRef} />
          <div className="gg3-bracket">
            {rounds.map(({ round, games }) => (
              <div className="gg3-round" key={round}>
                {games.map((g) => (
                  <GameCard
                    key={g.bracketGameId}
                    game={g}
                    showSeeds={showSeeds}
                    isAdmin={isAdmin}
                    onEnterScore={onEnterScore}
                    onEditGame={onEditGame}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
function GameCard({ game, showSeeds, isAdmin, onEnterScore, onEditGame }) {
  const isCompleted = game.gameStatus === "COMPLETED";
  const homeWinner = isCompleted && game.homeTeamScore > game.awayTeamScore;
  const awayWinner = isCompleted && game.awayTeamScore > game.homeTeamScore;

  const homeReal = game.homeTeamName && game.homeTeamName !== "TBD" && game.homeTeamName.trim() !== "";
  const awayReal = game.awayTeamName && game.awayTeamName !== "TBD" && game.awayTeamName.trim() !== "";
  const bothTeamsPresent = homeReal && awayReal;

  // Score entry is allowed whenever both teams are set — including on a
  // COMPLETED game, so an admin can correct a score that was entered wrong.
  const canScore = isAdmin && bothTeamsPresent;
  const canEdit = isAdmin; // ⭐ edit field/time allowed on ANY card, even empty ones

  // Placeholder for an empty slot, from the real template token on THIS game.
  const homePlaceholder = tokenToLabel(game.homeSeedToken);
  const awayPlaceholder = tokenToLabel(game.awaySeedToken);

  return (
    <div
      className={`gg3-game ${canScore ? "clickable" : ""} ${isCompleted ? "scored" : ""}`}
      data-game-id={game.bracketGameId}
      onClick={() => canScore && onEnterScore && onEnterScore(game)}
    >
      <TeamRow
        showSeeds={showSeeds}
        seed={game.homeTeamSeed}
        real={homeReal}
        name={homeReal ? game.homeTeamName : homePlaceholder}
        winner={homeWinner}
        isCompleted={isCompleted}
        score={game.homeTeamScore}
        rightMeta={!isCompleted ? (game.fieldNumber || "—") : null}
      />
      <TeamRow
        showSeeds={showSeeds}
        seed={game.awayTeamSeed}
        real={awayReal}
        name={awayReal ? game.awayTeamName : awayPlaceholder}
        winner={awayWinner}
        isCompleted={isCompleted}
        score={game.awayTeamScore}
        rightMeta={!isCompleted ? formatTime(game.gameDateTime) : null}
      />

      <div className="gg3-footer">
        {game.slotNum != null && <span className="gg3-slot-id">Slot {game.slotNum}</span>}
        {canEdit && (
          <button
            className="gg3-edit"
            title="Edit field / time"
            onClick={(e) => { e.stopPropagation(); onEditGame && onEditGame(game); }}
          >
            ✎
          </button>
        )}
      </div>
    </div>
  );
}

function TeamRow({ showSeeds, seed, real, name, winner, isCompleted, score, rightMeta }) {
  return (
    <div className={`gg3-row ${winner ? "winner" : ""}`}>
      {showSeeds && <span className="gg3-seed">{seed != null ? seed : ""}</span>}
      <span className={`gg3-name ${real ? "" : "gg3-placeholder"}`}>{name}</span>
      <span className="gg3-right">
        {isCompleted ? (
          <>
            {winner && <span className="gg3-check">✓</span>}
            <span className={`gg3-score ${winner ? "win" : ""}`}>{score != null ? score : "-"}</span>
          </>
        ) : (
          <span className="gg3-meta">{rightMeta}</span>
        )}
      </span>
    </div>
  );
}

// ---- helpers --------------------------------------------------------------
// Parse a template seed token into a human label:
//   "8"  -> real seed, no placeholder needed (returns null; team name shows)
//   "W8" -> "Winner of Slot 8"
//   "L4" -> "Loser of Slot 4"
function tokenToLabel(token) {
  if (!token) return "TBD";
  const t = String(token).trim();
  if (/^\d+$/.test(t)) return "TBD"; // plain seed number — real team fills this
  const m = t.match(/^([WL])\s*(\d+)$/i);
  if (m) {
    const kind = m[1].toUpperCase() === "W" ? "Winner" : "Loser";
    return `${kind} of Slot ${m[2]}`;
  }
  return t; // unknown format — show as-is
}

function formatTime(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleString("en-US", { hour: "numeric", minute: "2-digit" });
}

function drawConnectors(inner, svg, connections, zoom, color) {
  if (!inner || !svg) return;
  svg.innerHTML = "";
  svg.setAttribute("width", inner.offsetWidth);
  svg.setAttribute("height", inner.offsetHeight);
  const innerRect = inner.getBoundingClientRect();

  connections.forEach((conn) => {
    const fromEl = inner.querySelector(`[data-game-id="${conn.from}"]`);
    const toEl = inner.querySelector(`[data-game-id="${conn.to}"]`);
    if (!fromEl || !toEl) return;

    const fr = fromEl.getBoundingClientRect();
    const tr = toEl.getBoundingClientRect();

    const x1 = (fr.left - innerRect.left) / zoom + fr.width / zoom;
    const y1 = (fr.top - innerRect.top) / zoom + (fr.height / zoom) / 2;
    const x2 = (tr.left - innerRect.left) / zoom;
    const y2 = (tr.top - innerRect.top) / zoom + (tr.height / zoom) / 2;

    const toTarget = connections.filter((c) => c.to === conn.to);
    const idx = toTarget.indexOf(conn);
    const midX = x1 + 20 + idx * 20 + Math.abs(y2 - y1) / 15;

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`);
    path.setAttribute("stroke", color);
    path.setAttribute("stroke-width", "2");
    path.setAttribute("fill", "none");
    path.setAttribute("stroke-linecap", "round");
    path.setAttribute("stroke-linejoin", "round");
    svg.appendChild(path);
  });
}